import type { ReactNode } from 'react'

/**
 * Status → colour, in the customer's vocabulary.
 *
 * The console maps every internal state a shipment can hold. A customer sees a
 * narrower set and reads it differently: "in transit" is reassuring here and
 * merely factual there, and anything the transporter still owes them is amber
 * rather than neutral.
 */
const STATUS_COLOURS: Record<string, string> = {
  draft: 'grey',
  created: 'grey',
  pending: 'grey',
  confirmed: 'blue',
  planned: 'blue',
  ready_to_dispatch: 'cyan',
  in_shipment: 'purple',
  dispatched: 'purple',
  in_transit: 'purple',
  out_for_delivery: 'amber',
  arrived: 'cyan',
  delivered: 'green',
  completed: 'green',
  closed: 'green',
  approved: 'green',
  paid: 'green',
  issued: 'amber',
  partially_paid: 'amber',
  overdue: 'red',
  under_review: 'amber',
  raised: 'amber',
  investigating: 'amber',
  settled: 'green',
  failed: 'red',
  cancelled: 'red',
  rejected: 'red',
}

/**
 * Words a customer should not have to translate.
 *
 * "created" is an internal state name; to the person who placed the order the
 * consignment is booked. Anything not listed here already reads plainly.
 */
const STATUS_WORDS: Record<string, string> = {
  created: 'booked',
  confirmed: 'booked',
  planned: 'planned',
  ready_to_dispatch: 'awaiting pickup',
  in_shipment: 'booked',
  dispatched: 'picked up',
  out_for_delivery: 'out for delivery',
  partially_paid: 'part paid',
  under_review: 'under review',
  failed: 'not delivered',
}

export function StatusChip({ status }: { status: string }) {
  const colour = STATUS_COLOURS[status] ?? 'grey'
  const word = STATUS_WORDS[status] ?? status.replace(/_/g, ' ')
  return <span className={`chip chip-${colour}`}>{word}</span>
}

export function Panel({
  title,
  action,
  children,
  bodyless,
}: {
  title?: string
  action?: ReactNode
  children: ReactNode
  bodyless?: boolean
}) {
  return (
    <div className="panel">
      {title && (
        <div className="panel-head">
          <span className="panel-title">{title}</span>
          {action}
        </div>
      )}
      {bodyless ? children : <div className="panel-body">{children}</div>}
    </div>
  )
}

export function Kpi({
  value,
  label,
  foot,
  tone,
}: {
  value: ReactNode
  label: string
  foot?: string
  tone?: 'alert' | 'warn' | 'good'
}) {
  return (
    <div className={`kpi ${tone ?? ''}`}>
      <span className="kpi-value">{value}</span>
      <span className="kpi-label">{label}</span>
      {foot && <span className="kpi-foot">{foot}</span>}
    </div>
  )
}

export function Empty({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="empty">
      <div className="empty-title">{title}</div>
      {hint && <div>{hint}</div>}
    </div>
  )
}

export function Loading({ rows = 4 }: { rows?: number }) {
  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 9 }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="skeleton" style={{ height: 32 }} />
      ))}
    </div>
  )
}

export function ErrorBanner({ error }: { error: unknown }) {
  if (!error) return null
  const message = error instanceof Error ? error.message : String(error)
  const requestId = (error as { requestId?: string })?.requestId
  return (
    <div className="banner banner-error">
      {message}
      {/* Worth showing: it is the one thing that lets the transporter's support
          desk find this exact request in the log. */}
      {requestId && (
        <span className="mono" style={{ opacity: 0.7 }}>
          {' '}
          · {requestId}
        </span>
      )}
    </div>
  )
}

/*
 * Formatting mirrors the console's on purpose: both render the same rupees for
 * the same tenant, and an invoice that reads ₹1,23,456.00 on one screen and
 * ₹123,456 on the other looks like two different amounts.
 */

export function inr(value: string | number | null | undefined): string {
  const n = typeof value === 'string' ? Number(value) : (value ?? 0)
  if (!Number.isFinite(n)) return '—'
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export function num(value: string | number | null | undefined, digits = 0): string {
  const n = typeof value === 'string' ? Number(value) : (value ?? 0)
  if (!Number.isFinite(n)) return '—'
  return n.toLocaleString('en-IN', { maximumFractionDigits: digits, minimumFractionDigits: digits })
}

export function dateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

export function dateOnly(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

/** Whole hours and minutes, for a delay a customer is reading as an apology. */
export function duration(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || minutes <= 0) return 'on time'
  const h = Math.floor(minutes / 60)
  const m = Math.round(minutes % 60)
  return h > 0 ? `${h}h ${m}m late` : `${m}m late`
}

export function daysUntil(date: string | null | undefined): number | null {
  if (!date) return null
  const target = new Date(date).getTime()
  if (Number.isNaN(target)) return null
  return Math.ceil((target - Date.now()) / 86_400_000)
}

/**
 * A due date as a countdown. "Due 15 Sep" makes the reader do the arithmetic,
 * and the arithmetic is the whole point of the column.
 */
export function DueChip({ date, balance }: { date: string | null | undefined; balance: string }) {
  if (Number(balance) <= 0) return <span className="chip chip-green">settled</span>

  const days = daysUntil(date)
  if (days === null) return <span style={{ color: 'var(--text-faint)' }}>—</span>

  if (days < 0) return <span className="chip chip-red">{Math.abs(days)}d overdue</span>
  if (days <= 7) return <span className="chip chip-amber">due in {days}d</span>
  return <span className="chip chip-grey">due in {days}d</span>
}
